import styled from 'styled-components';
import { black, exactWhite } from '../../utils/style/colors';
import SanitizedHTML from 'react-sanitized-html';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@reach/router';
import {
  IProvider,
  IProviderItems,
  IProviderItemView,
  ITagView,
} from '../../store/providers/providerTypes';
import { requireDocumentInFolder } from '../../store/providers/providerSlice';
import { appDispatch } from '../../store/store';
import { getDomainAndPort } from 'api/Utils';

const Burger = styled.div`
  position: absolute;
  top: ${(props) => (props.open ? '1rem' : '2.5rem')};
  right: 1rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  cursor: pointer;
  z-index: 110;
  transition: all ease-in-out 0.25s;
  div {
    width: 2rem;
    height: 0.25rem;
    background: ${black};
    border-radius: 10px;
    transition: all 0.3s linear;
    transform-origin: 1px;

    :first-child {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }

    :nth-child(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
    }

    :nth-child(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }

  @media screen and (max-width: 800px) {
    top: 2.5rem;
    right: 1rem;
  }
`;

const MenuFixedLocation = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  z-index: 1;
  transition:
    color 0.25s ease-in-out,
    all 0.5s ease-in-out;
  color: ${black};
  pointer-events: auto;
  width: ${(props) => (props.isOpen ? '25rem' : '0')};
  background: var(--burger-menu-bg);
  align-items: start;
  color: ${black};
  padding: ${(props) => (props.isOpen ? '1rem' : '0')};
  padding-top: 5rem;
  cursor: pointer;

  &:hover {
    color: ${black};
  }

  &:has(> ul:hover) {
    background: var(--burger-menu-bg);
    color: ${black};
  }

  /* dark mode */

  //&:has(> ul:hover) {
  //  background: linear-gradient(to left, #f1f0f0, transparent);
  //}

  /* @media screen and (max-width: 800px) {
    pointer-events: auto;
    width: ${(props) => (props.isOpen ? '25rem' : '0')};
    background: rgb(0 0 0 / 80%);
    align-items: start;
    color: ${black};
    padding: ${(props) => (props.isOpen ? '1rem' : '0')};
    padding-top: 5rem;

    &:hover {
      color: ${black};
    }

    &:has(> ul:hover) {
      background: rgb(0 0 0 / 80%);
      color: ${exactWhite};
    }
  } */
`;

const MenuItemContainer = styled.ul`
  pointer-events: all;
  margin-inline-start: 0.5rem;
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow: hidden auto;
`;

const MenuItem = styled.li`
  display: flex;
  width: 100%;
  cursor: pointer;
  padding: 0.5rem 0.1rem;
  transition: all 0.5s ease-in-out;
  transition: all 1s ease;
  font-size: 1.1rem;

  &:hover {
    cursor: pointer;
    transform: translateX(-5px) scale(1.05);
  }
`;

const Logo = styled.img`
  height: 1.5rem !important;
  width: 1.5rem !important;
  margin-inline-end: 0.5rem;
  color: ${black};
`;

export interface IHeaderItem {
  id: string | number;
  title: string | null | undefined;
  isShine: boolean;
  tags: ITagView[];
  logo?: string;
}

interface IProps {
  itemList: IHeaderItem[] | undefined;
  provider: IProvider;
  allItems: IProviderItems;
  onResetItemArrangement: () => void;
  homeFolder?: number;
  providerId?: string | undefined;
  providerNameInURL?: string | undefined;
}

export const WINDOW_WIDTH = 800;

const MenuWithBurger: React.FC<IProps> = ({
  itemList,
  provider,
  allItems,
  onResetItemArrangement,
  homeFolder,
  providerId,
  providerNameInURL,
}) => {
  const [open, setOpen] = useState(false);
  const [_, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const location = useLocation();

  const pageRef = useRef<HTMLDivElement | null>(null);

  const handleClick = (id: number | string) => {
    const params = new URLSearchParams(location.search);
    const domain = getDomainAndPort();
    const homeProfileId = domain?.homeProfile;

    const urlProviderId =
      homeProfileId === providerId &&
      location.href.includes(providerNameInURL || '')
        ? providerNameInURL
        : providerId
          ? providerId
          : provider.id;
    if (params.has('more')) {
      if (itemList && itemList.find((el) => el.id === id)!.isShine) {
        navigate(`/${urlProviderId}/f/${id}`);
      } else {
        onResetItemArrangement();
        window.location.hash = 'menu';
        setTimeout(() => {
          document
            .getElementById(`${id}`)
            ?.scrollIntoView({ behavior: 'smooth' });
        }, 250);
      }
    } else if (location.pathname !== `/${urlProviderId}`) {
      if ((homeFolder && !urlProviderId) || homeProfileId === urlProviderId) {
        navigate('/');
      } else {
        navigate(`/${urlProviderId}`);
      }
      setTimeout(() => {
        document
          .getElementById(`${id}`)
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    } else {
      if (itemList && itemList.find((el) => el.id === id)!.isShine) {
        navigate(`/${urlProviderId}/f/${id}`);
      } else {
        document
          .getElementById(`${id}`)
          ?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    const handleWindowChange = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleWindowChange);

    return () => window.removeEventListener('resize', handleWindowChange);
  }, []);

  useEffect(() => {
    provider.rootDocument.id &&
      appDispatch(
        requireDocumentInFolder({
          folderId: homeFolder ? homeFolder : provider.rootDocument.id,
          providerId: provider.id,
        })
      ).then((res: any) => {
        res?.items
          ?.filter((it: IProviderItemView) => {
            return it.tags.some((tag) => tag.text === 'conf_shine');
          })
          ?.filter((it: IProviderItemView) =>
            it.tags.some((tag) => tag.text.toLowerCase() !== 'conf_hidden')
          )
          ?.forEach((it: IProviderItemView) => {
            appDispatch(
              requireDocumentInFolder({
                folderId: it.id,
                providerId: provider.id,
              })
            );
          });
      });
  }, [provider.id, homeFolder]);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (pageRef.current && !pageRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // const showMenu = open || windowWidth > WINDOW_WIDTH;
  const showMenu = open;
  return (
    <MenuFixedLocation isOpen={open} ref={pageRef}>
      {/* {windowWidth < WINDOW_WIDTH && (
        <Burger onClick={() => setOpen(!open)} open={open}>
          <div />
          <div />
          <div />
        </Burger>
      )} */}
      <Burger onClick={() => setOpen(!open)} open={open}>
        <div />
        <div />
        <div />
      </Burger>
      <MenuItemContainer>
        {showMenu &&
          itemList?.map((header) => (
            <MenuItem key={header.id} onClick={() => handleClick(header.id)}>
              {/* {windowWidth < WINDOW_WIDTH &&
                allItems.items[header.id].data.thumbnailUrl && (
                  <Logo
                    src={`${allItems.items[header.id].data.thumbnailUrl}`}
                  />
                )} */}
              {allItems.items[header.id].data.thumbnailUrl && (
                <Logo src={`${allItems.items[header.id].data.thumbnailUrl}`} />
              )}
              <SanitizedHTML
                allowedAttributes={{ span: ['style'] }}
                html={header.title}
              />
            </MenuItem>
          ))}
      </MenuItemContainer>
    </MenuFixedLocation>
  );
};

export default MenuWithBurger;
